/**
 * Tarifa de venta: precio por unidad y descuento por volumen.
 *
 * Es la única fuente de la verdad. El precio que acaba cobrando Stripe no sale de
 * aquí directamente —el servidor nunca se fía del navegador—, pero sí sale de este
 * mismo cálculo: `scripts/export-catalog.mjs` vuelca los tramos ya resueltos en
 * server/data/catalog.json y el backend elige el que corresponde a la cantidad.
 * Si esta tabla cambia, hay que regenerar el catálogo (`npm run catalog:export`).
 *
 * Todo se calcula en céntimos: 15,95 − 0,30 en coma flotante da 15,649999999999999.
 */

/** A partir de esta cifra de caladas el dispositivo va a la tarifa alta. */
export const HIGH_PUFFS_THRESHOLD = 40000;

export const UNIT_PRICE_HIGH_CENTS = 1895;
export const UNIT_PRICE_STANDARD_CENTS = 1595;

/** Cada escalón de volumen descuenta esto por unidad. */
export const VOLUME_STEP_CENTS = 30;

/** Los tamaños que ofrece la ficha de producto, de menor a mayor. */
export const PACK_SIZES = [1, 10, 50, 100] as const;

/** Precio de tarifa de una unidad suelta, según la autonomía declarada. */
export function baseUnitPriceCents(puffs?: number): number {
  return puffs !== undefined && puffs >= HIGH_PUFFS_THRESHOLD
    ? UNIT_PRICE_HIGH_CENTS
    : UNIT_PRICE_STANDARD_CENTS;
}

/**
 * Escalones de volumen. No es proporcional a la cantidad: se cuentan los tramos
 * alcanzados (10, 50 y 100), y cada uno resta 0,30 € a la unidad.
 */
export function volumeStepsForQty(qty: number): number {
  if (qty >= 100) return 3;
  if (qty >= 50) return 2;
  if (qty >= 10) return 1;
  return 0;
}

/** Precio unitario ya con el descuento por volumen que corresponda a esa cantidad. */
export function unitPriceCentsForQty(baseCents: number, qty: number): number {
  return Math.max(0, baseCents - volumeStepsForQty(qty) * VOLUME_STEP_CENTS);
}

/** Importe total de una línea: cantidad por el precio unitario de su tramo. */
export function lineTotalCents(baseCents: number, qty: number): number {
  return unitPriceCentsForQty(baseCents, qty) * qty;
}

/** Los tramos de un producto, tal como se vuelcan al catálogo del servidor. */
export function priceTiersFor(baseCents: number) {
  return PACK_SIZES.map((minQty) => ({
    minQty,
    unitAmountCents: unitPriceCentsForQty(baseCents, minQty),
  }));
}

export const toEuros = (cents: number) => cents / 100;
